/* eslint-disable react-refresh/only-export-components -- intentional: il file esporta il componente e il hook `useSezioniDelGioco` (con i suoi due tipi), che la v3 legge per la Mode Rail e la griglia; resta qui perche' i cancelli `tools/verify-*.ts` leggono il sorgente di questo path. */
import {
  useEffect,
  useMemo,
  useRef,
  type ComponentType,
  type SVGProps,
  type TouchEvent,
  type UIEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, History, Play } from 'lucide-react';

import {
  BattlesSwordIcon,
  MissionsChecklistIcon,
  DecksCardsIcon,
  EventsTrophyIcon,
  LiveBroadcastIcon,
  RankedSwordsIcon,
  RankingsChartIcon,
  SitngoBoltIcon,
} from './HubIcons';

import { useHasLogtoRole } from '@/hooks/useHasLogtoRole';
import { env } from '@/env';
import { useGameSelection } from '@/hooks/useGameSelection';
import { useHaptics } from '@/hooks/useHaptics';
import { useOspite } from '@/hooks/useOspite';
import { RichiedeAccesso } from '@/components/common/RichiedeAccesso';
import { featuresForGame } from '@/lib/gameFeatures';
import { cn } from '@/lib/cn';
import { ETICHETTA_SCHEDA, RIGA_SCHEDE, SCHEDA_STRETTA } from '@/components/common/schedeSezione';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setHubSection } from '@/store/core.slice';
import { setBannerCompact } from '@/store/settings.slice';
import { BOUNCE, useSlidingIndicator } from '@/hooks/useSlidingIndicator';
import { BannerSideSlot } from '@/components/layout/BannerSlots';
import { CodeTorneiPanel } from './panels/CodeTorneiPanel';
import { RankedPanel } from './panels/RankedPanel';
import { EventsPanel } from './panels/EventsPanel';
import { SitngoPanel } from './panels/SitngoPanel';
import { MissionsPanel } from './panels/MissionsPanel';
import { LeaderboardPanel } from './panels/LeaderboardPanel';
import { LeghePanel } from './panels/LeghePanel';
import { BattlesPanel } from './panels/BattlesPanel';
import { DecksPanel } from './panels/DecksPanel';
import { LiveMatchesPanel } from './panels/LiveMatchesPanel';
import { CrLiveLimuloPanel } from './panels/CrLiveLimuloPanel';
import { QcpPlayPanel } from './panels/QcpPlayPanel';
import { QcpLeaderboardPanel } from './panels/QcpLeaderboardPanel';
import { QcpMatchesPanel } from './panels/QcpMatchesPanel';
import { HubBridge } from '@/components/bridge/HubBridge';
import { HubBurraco } from '@/components/burraco/HubBurraco';
import { HubBriscola } from '@/components/briscola/HubBriscola';
import { HubScopa } from '@/components/scopa/HubScopa';
import { HubGinRummy } from '@/components/ginrummy/HubGinRummy';
import { HubScala40 } from '@/components/scala40/HubScala40';
import { HubSettemezzo } from '@/components/settemezzo/HubSettemezzo';
import { HubTressette } from '@/components/tressette/HubTressette';
import { HubPilotta } from '@/components/pilotta/HubPilotta';
import { HubMpirimpa } from '@/components/mpirimpa/HubMpirimpa';
import { HubSolitari } from '@/components/solitario/HubSolitari';
import { backgammonSectionDefs } from '@/components/backgammon/lobbySections';
import { chessSectionDefs } from '@/components/chess/sections';
import { chessLimuloAcceso } from '@/lib/chess/chessLimuloAcceso';
import { qcpAcceso } from '@/lib/qcp/qcpAcceso';

export type SectionKey =
  | 'ranked'
  | 'events'
  | 'sitngo'
  | 'missions'
  | 'rankings'
  | 'decks'
  | 'battles'
  | 'live'
  | 'play'
  | 'tournaments'
  | 'matches'
  | 'myBoards'
  | 'stats'
  | 'bot';

export interface SectionDef {
  key: SectionKey;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  Panel: ComponentType;
  /**
   * The panel brings its own scroller (pinned header + scrolling body). The
   * section wrapper then clips instead of scrolling, so the two don't nest —
   * an outer scroller would drag the header the inner one just pinned.
   * The wrapper is a flex column in that case: the panel's root must take the
   * height with `flex-1 min-h-0` or `h-full`, and scroll inside it — a root
   * that just grows with its content is clipped with no way to reach the end.
   */
  ownScroll?: boolean;
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * L'ELENCO DELLE SEZIONI DEL GIOCO ATTIVO e le serrature dei giochi che hanno una Hub tutta loro, estratti da `HubSections` come
 * hook (v3 fase 2, `tma-orc-16-v3-chrome`, PR «sezioni-estratte»): li legge la riga di bottoni di oggi (`HubSections`) e, nella v3,
 * la Mode Rail e la griglia delle modalita'. Refactor PURO: il corpo e' quello di prima, riga per riga, e chi aggiunge o toglie una
 * scheda lo fa QUI e basta (una scheda scritta a mano in un solo posto riapre la divergenza). Resta in questo file perche' i cancelli
 * (`tools/verify-*.ts`) leggono il sorgente di `HubSections.tsx` per path.
 * `showBridge`, `showBurraco`, `showScopa`, `showBriscola`, `showGinRummy`, `showScala40`, `showSettemezzo`, `showTressette`, `showPilotta`, `showMpirimpa` e `showSolitario` sono i giochi senza riga di sezioni: la Hub
 * monta il loro componente (il confronto LETTERALE del flag sta dove si monta, in `HubSections`).
 */
export function useSezioniDelGioco() {
  const { t } = useTranslation('hub');
  const { activeGame } = useGameSelection();
  const features = featuresForGame(activeGame);
  const showCr = features.cr && env.featureClashRoyaleEnabled;
  // ⚠️ Backgammon vuole il ruolo `beta` OLTRE al flag, e la tile e' meta' della
  // serratura: l'altra meta' sono le rotte in `routes/router.tsx`. Proteggere
  // solo le rotte lascerebbe una tile che porta a un'area vuota — un vicolo
  // cieco, non una protezione; proteggere solo la tile lascerebbe la sezione
  // raggiungibile per deep-link. Il flag da solo non basta perche' e' GLOBALE:
  // un utente col ruolo `user` porta ZERO scope sulla resource QGammon.
  // Forma inline (non <RoleGate>) perche' qui serve un BOOLEANO che entra in una
  // condizione, non un wrapper attorno a un sottoalbero — e' l'uso documentato
  // in `components/common/RoleGate.tsx`.
  const isBeta = useHasLogtoRole('beta');
  const showQgammon = features.backgammon && env.featureBackgammonEnabled && isBeta;
  // Gli scacchi su Limulo (E6 di ADR-0193, P3): stessa serratura del backgammon — il predicato (flag suo E Limulo) E il ruolo
  // `beta`. ⛔ Il vecchio `/chess` (Lichess, `featureChessEnabled`) non passa di qui: con il predicato spento, cioe' in
  // produzione, la Hub degli scacchi e' quella di prima, byte per byte.
  const showChess = features.chess && chessLimuloAcceso() && isBeta;
  // QChinesePoker (T0): stessa serratura del backgammon e degli scacchi — il predicato (opt-in, spento ovunque fino a T2) E il ruolo `beta`.
  const showQcp = features.chinesePoker && qcpAcceso() && isBeta;
  // QBridge (I-609): la Hub del Bridge ha SOLO la tessera. Stessa serratura delle altre (il gioco attivo E il ruolo `beta`: il gioco c'e' nel selettore solo per
  // chi ce l'ha, ma una selezione salvata non e' una serratura); il flag e' il confronto LETTERALE nel return, dove si monta.
  const showBridge = features.bridge && isBeta;
  // QBurraco (ingresso, PR1): stessa serratura — il gioco attivo, il ruolo `beta` E Limulo acceso (le rotte del tavolo vero e le letture di «I miei tavoli» e dei
  // tornei passano dal suo BFF). Il flag e' il confronto LETTERALE nel return, dove si monta. La Hub del Burraco non ha la riga delle sezioni.
  const showBurraco = features.burraco && isBeta && env.featureLimuloEnabled;
  // La SCOPA (kit-giochi S4): la stessa serratura del Burraco (il gioco attivo, `beta`, Limulo); il flag e' il confronto LETTERALE nel return, dove si monta.
  const showScopa = features.scopa && isBeta && env.featureLimuloEnabled;
  // La BRISCOLA (kit-giochi P6): la stessa serratura della scopa (il gioco attivo, `beta`, Limulo); il flag e' il confronto LETTERALE nel return, dove si monta.
  const showBriscola = features.briscola && isBeta && env.featureLimuloEnabled;
  // Il GIN RUMMY (P6 del contratto del gin): la stessa serratura della scopa (il gioco attivo, `beta`, Limulo); il flag e' il confronto LETTERALE nel return, dove si monta.
  const showGinRummy = features.ginRummy && isBeta && env.featureLimuloEnabled;
  // La SCALA 40 (P6 del contratto della scala 40): la stessa serratura del gin (il gioco attivo, `beta`, Limulo); il flag e' il confronto LETTERALE nel return, dove si monta.
  const showScala40 = features.scala40 && isBeta && env.featureLimuloEnabled;
  // Il SETTE E MEZZO (P6 del contratto del sette e mezzo): la stessa serratura del gin (il gioco attivo, `beta`, Limulo); il flag e' il confronto LETTERALE nel return, dove si monta.
  const showSettemezzo = features.settemezzo && isBeta && env.featureLimuloEnabled;
  // Il TRESSETTE (P6 del contratto del tressette): la stessa serratura del sette e mezzo (il gioco attivo, `beta`, Limulo); il flag e' il confronto LETTERALE nel return, dove si monta.
  const showTressette = features.tressette && isBeta && env.featureLimuloEnabled;
  // La PILOTTA (P8 del contratto della Pilotta): la stessa serratura del tressette (il gioco attivo, `beta`, Limulo); il flag e' il confronto LETTERALE nel return, dove si monta.
  const showPilotta = features.pilotta && isBeta && env.featureLimuloEnabled;
  // Il BIRIBA (PR-A, lo scheletro): la stessa serratura della pilotta (il gioco attivo, `beta`, Limulo); il flag e' il confronto LETTERALE nel return, dove si monta.
  const showMpirimpa = features.mpirimpa && isBeta && env.featureLimuloEnabled;
  // Il SOLITARIO (TMA PR 1): la stessa serratura del Burraco (il gioco attivo, `beta`, Limulo); il flag e' il confronto LETTERALE nel return, dove si monta.
  const showSolitario = features.solitario && isBeta && env.featureLimuloEnabled;

  // NB: il livello/EXP del giocatore NON sta più qui. Stava dentro il bottone
  // "Ranked", che è un elemento di navigazione: un bottone che cambia sezione
  // non è il posto dove leggere uno stato. Ora vive nell'intestazione del
  // pannello Ranked, accanto al rank a cui appartiene.

  const sections: SectionDef[] = useMemo(() => {
    // Chinese Poker is a full-surface game: its three sections REPLACE the
    // cross-game row (Events/Rankings-Laravel/Live would all be empty for the
    // synthetic game id). Other games keep the existing behavior untouched.
    if (showQcp) {
      return [
        {
          key: 'play',
          Icon: Play,
          label: t('sections.play', { defaultValue: 'Play' }),
          Panel: QcpPlayPanel,
          ownScroll: true,
        },
        {
          key: 'rankings',
          Icon: BarChart3,
          label: t('sections.rankings', { defaultValue: 'Rankings' }),
          Panel: QcpLeaderboardPanel,
        },
        {
          key: 'matches',
          Icon: History,
          label: t('sections.matches', { defaultValue: 'Matches' }),
          Panel: QcpMatchesPanel,
          ownScroll: true,
        },
      ];
    }
    // Backgammon (QGammon) is likewise a full-surface game: its sections REPLACE the
    // cross-game row for the same reason as Chinese Poker above.
    // 🔴 L'ELENCO NON E' PIU' SCRITTO QUI: e' lo stesso della rotta `/backgammon`
    // (`components/backgammon/lobbySections.ts`) — «4 uguali alla hub», Luca 23/09. Una scheda
    // aggiunta a mano solo qui riaprirebbe la divergenza che quel file esiste per chiudere.
    if (showQgammon) {
      return backgammonSectionDefs(t, { tournaments: env.featureLimuloEnabled });
    }
    // Gli scacchi su Limulo SOSTITUISCONO la riga cross-game come il backgammon: Eventi/Classifiche/Live del monolite sarebbero
    // vuoti per il gioco sintetico. L'elenco sta in `components/chess/sections.ts`.
    if (showChess) {
      return chessSectionDefs(t);
    }
    const list: SectionDef[] = [];
    // Row 1: Ranked · Events · Sit&Go · Missioni.
    // Row 2: Rankings · Decks · Battles · Live.
    // Events / Rankings / Live are cross-game; the rest are CR surfaces (QDuels
    // is Clash-Royale-only) and drop out under another game.
    if (showCr) {
      // 🔴 «Ranked» = LE CODE DI qTOURNAMENTS a flag acceso (decisione di Luca 2026-09-25 ~17:42Z:
      // «le code vanno nella sezione ranked hub di clash royale»; contratto kb#282). A flag spento
      // resta `RankedPanel` di QDuels com'e' — anche in produzione, dove QDuels e' acceso: la
      // dismissione di QDuels (L-2213) e' un atto a se', non un effetto collaterale di questa scheda.
      // In AND con Limulo: le code passano dal suo BFF (come il provider in `CodeTorneiContext`).
      const codeTornei = env.featureTournamentQueuesEnabled && env.featureLimuloEnabled;
      list.push({
        key: 'ranked',
        Icon: RankedSwordsIcon,
        label: t('sections.ranked', { defaultValue: 'Ranked' }),
        Panel: codeTornei ? CodeTorneiPanel : RankedPanel,
      });
    }
    list.push({
      key: 'events',
      Icon: EventsTrophyIcon,
      label: t('sections.events', { defaultValue: 'Events' }),
      Panel: EventsPanel,
      ownScroll: true,
    });
    if (showCr) {
      list.push({
        key: 'sitngo',
        Icon: SitngoBoltIcon,
        label: t('sections.sitngo', { defaultValue: 'Sit&Go' }),
        Panel: SitngoPanel,
        ownScroll: true,
      });
    }
    // ⚠️ NON e' gattata dietro un flag, ed e' voluto: qui ci sono le missioni VERE, non
    // un'anteprima. CriGio aveva portato in Hub un mockup coi numeri scritti a mano; Luca
    // ha scelto l'altra strada il 2026-09-09T23:08:39Z (voce L-735, opzione A), e il
    // pannello ora monta la STESSA lista di `/settings/missions` — servizio Quest,
    // `GET /v1/missions`, claim incluso. Non c'e' niente da spegnere: se Quest non
    // risponde lo dice il pannello, che ha i suoi tre stati.
    //
    // 🔴 E LA CONSEGUENZA, che e' la parte che morde: `VITE_FEATURE_MISSIONS_ENABLED`
    // (in `env.ts` come `featureMissionsEnabled`) NON arriva fin qui. Spegnerlo toglie la
    // rotta `settings/missions` e la voce in Impostazioni, e lascia in piedi QUESTA scheda
    // e il bottone di claim dentro `MissionRow`. Chi vede il flag a `false` e conclude
    // «le missioni sono spente» ha guardato la porta di servizio: quella principale e'
    // questa, ed e' aperta. (Lo dice anche il passo 1-bis/7 di `infra/stg-ovh/deploy.sh`,
    // che pretende il flag spento e dichiara di non coprire questo pannello.)
    // ⚠️ E dove va il claim lo decide il BACKEND delle missioni (`env.missionsBackend`, che
    // `questApi.ts` usa per la lista e per il claim: I-223 / L-2317). Con `limulo` (oggi
    // `.env.staging`) va a Limulo. Con `quest` va a `env.questApiUrl`, e se l'env della build non
    // nomina `VITE_QUEST_API_URL` cade sul fallback cablato, che e' la PRODUZIONE: in produzione
    // e' voluto, da staging e' il caso di `.env.staging-prodtenant`. ⇒ la scheda scrive sul Quest
    // vero solo li': da quella build un claim muove essenze vere, e' denaro vero, e resta un GO
    // di Luca (CLAUDE.md, «Restano GO di Luca»).
    // ⭐ ***Un flag che porta il nome della funzione e ne copre solo una parte e' peggio di
    // un flag assente: chi lo spegne smette di cercare.***
    // [D 2026-09-15 per il fallback di `questApiUrl`, KB `services/qlash-telegram-app/overview.md`;
    //  il backend del claim e' dal codice (`questApi.ts`, I-223 / L-2317), non misurato sul servito:
    //  lo misura M0, collaudo della PR 3.]
    // ⚓ QUEST_STG_PROD_FALLBACK — vero solo FINCHE' regge la relazione: almeno un file env di
    //   staging (oggi `.env.staging-prodtenant`) ha il backend `quest` e non nomina
    //   `VITE_QUEST_API_URL`, e questa scheda resta montata senza gate. Il giorno che una di queste
    //   condizioni cambia, `tools/verify-missions-quest.ts` diventa ROSSO e questo blocco si riscrive o si
    //   toglie — un avviso scaduto e' peggio del silenzio, perche' chi lo legge smette di guardare.
    //
    // La regola scritta piu' sotto («un pannello che puo' renderizzare `null` non deve
    // avere una scheda») resta rispettata, ed e' stata ri-verificata dopo la sostituzione:
    // `MissionsPanel` non ha nessun `return null` e `MissionsList` su lista vuota rende
    // `<MissionsEmpty />`. Al 2026-09-09 quelli che possono rendere `null` sono
    // LeaderboardPanel, QcpMatchesPanel e TournamentLadder.
    // Missioni prende il posto che era di Community (finita dentro Amici), ma
    // FUORI dal blocco Clash Royale: i traguardi non sono di un gioco solo, e
    // sotto un altro gioco Community spariva mentre queste devono restare.
    list.push({
      key: 'missions',
      Icon: MissionsChecklistIcon,
      label: t('sections.missions', { defaultValue: 'Missions' }),
      Panel: MissionsPanel,
      // `false`, e non e' il valore che aveva il mockup: `MissionsList` e' una lista
      // semplice e NON porta uno scroller proprio. Con `ownScroll: true` il contenitore
      // esterno passa a `overflow-hidden` (v. sotto) e una lista piu' alta dello schermo
      // resterebbe TAGLIATA senza modo di scorrerla.
      ownScroll: false,
    });
    // 🔴 «Rankings» nella Hub CR = LE LEGHE di qLeagues a flag acceso (Luca, I-224 → L-2312 (a),
    // 2026-09-27: «rivedi sezione rankings, che è ancora colelgato a laravel: disabilita questo
    // tipologia di classifica ed implementa le leghe limulo», testuale). Stessa chiave e stesso posto della
    // scheda Laravel, che resta solo dove il flag e' spento — cioe' in produzione (L-2313 (a)).
    // In AND con Limulo (le rotte stagione sono del suo BFF) e con CR (la lega oggi esiste solo
    // per CR 1v1): sotto un altro gioco decide `featureLeaderboardEnabled`, come prima — e su
    // stg-tma, dove quel flag e' ora spento, sotto un altro gioco la scheda non c'e' piu'.
    const legheLimulo = showCr && env.featureLegheEnabled && env.featureLimuloEnabled;
    // 🔴 LA SCHEDA SEGUE IL PANNELLO, e prima non lo faceva. `LeaderboardPanel` si spegne
    // da se' col flag (`LeaderboardPanel.tsx:67`, `return null`) — corretto — ma la scheda
    // veniva offerta comunque: su una build con la classifica spenta l'utente toccava
    // «CLASSIFICA» e otteneva **uno schermo vuoto**. Non un errore, non un vuoto
    // dichiarato: NIENTE.
    //
    // 📌 E' la terza forma dello stesso guasto trovata il 2026-09-05, e la peggiore delle
    // tre: un vuoto affermato (#133) dice una cosa falsa, uno spinner non dice niente ma si
    // vede che sta lavorando — **una schermata vuota non dichiara nemmeno di esistere**, e
    // nessun controllo la intercetta, perche' non parte nessuna chiamata e non fallisce
    // nulla. L'ha trovata `or45` guardando l'`innerText` del body, non la rete.
    //
    // ⇒ La regola: **un pannello che puo' renderizzare `null` non deve avere una scheda.**
    // Chi aggiunge un `return null` in cima a un pannello del Hub gatta ANCHE il push qui,
    // con la stessa condizione. Oggi e' l'unico caso — verificato con un grep su tutti i
    // pannelli — ed e' scritto qui perche' resti l'unico.
    if (legheLimulo) {
      list.push({
        key: 'rankings',
        Icon: RankingsChartIcon,
        label: t('sections.rankings', { defaultValue: 'Rankings' }),
        Panel: LeghePanel,
        // Come la scheda Laravel: card-finestra con lo scroller dentro.
        ownScroll: true,
      });
    } else if (env.featureLeaderboardEnabled) {
      list.push({
        key: 'rankings',
        Icon: RankingsChartIcon,
        label: t('sections.rankings', { defaultValue: 'Rankings' }),
        Panel: LeaderboardPanel,
        // Ora porta la propria card-finestra con lo scroller dentro: il
        // contenitore esterno deve CLIPPARE, non scorrere, altrimenti i due
        // scroller si annidano e il gesto finisce su quello sbagliato.
        ownScroll: true,
      });
    }
    if (showCr) {
      list.push(
        {
          key: 'decks',
          Icon: DecksCardsIcon,
          label: t('sections.decks', { defaultValue: 'Decks' }),
          Panel: DecksPanel,
          ownScroll: true,
        },
        {
          key: 'battles',
          Icon: BattlesSwordIcon,
          label: t('sections.battles', { defaultValue: 'Battles' }),
          Panel: BattlesPanel,
          ownScroll: true,
        },
      );
    }
    // 🔴 «Live» sotto Clash Royale = le partite CR in corso sui tornei di LIMULO a flag acceso (Luca, I-224;
    // L-2314 a, L-2315 a, L-2316 b): code, sit&go, tornei, sfide. A flag spento, e sotto gli altri giochi,
    // resta `LiveMatchesPanel` di QDuels com'e' — anche in produzione: la dismissione di QDuels e' un atto
    // a se'. In AND con Limulo, come le code. Un punto solo: `verify-limulo.ts` lo pretende.
    const liveLimulo = showCr && env.featureCrLiveLimuloEnabled && env.featureLimuloEnabled;
    list.push({
      key: 'live',
      Icon: LiveBroadcastIcon,
      label: t('sections.live', { defaultValue: 'Live' }),
      Panel: liveLimulo ? CrLiveLimuloPanel : LiveMatchesPanel,
    });
    return list;
  }, [t, showCr, showQcp, showQgammon, showChess]);

  // I giochi senza riga di sezioni: la Hub mostra il componente del gioco (la catena `if (showX) return …` di `HubSections`). UN'espressione sola, per chi deve sapere se un gioco ha la
  // griglia (la Mode Rail della v3, `v3/chrome/modalita.ts`): un gioco nuovo si aggiunge QUI e nella catena, e `tests/v3-mode-rail.test.tsx` legge il sorgente per pretenderlo.
  const senzaGriglia = showBridge || showBurraco || showScopa || showBriscola || showGinRummy || showScala40 || showSettemezzo || showTressette || showPilotta || showMpirimpa || showSolitario;
  return { sections, senzaGriglia, showCr, showBridge, showBurraco, showScopa, showBriscola, showGinRummy, showScala40, showSettemezzo, showTressette, showPilotta, showMpirimpa, showSolitario };
}

/**
 * The Hub's section switcher: a row of buttons under the banner, and the
 * selected section's screen right below them — no navigation, the Hub IS the
 * screen.
 *
 * Sections are game-conditional: "Mie battaglie" and "Top Decks" are Clash
 * Royale things and would land on an empty screen under Chess. Each game brings
 * its own row.
 *
 * The button row stays put; only the panel scrolls. Same contract as the
 * Tournaments tabs — the thing you navigate with must never slide away under
 * your thumb.
 */
export function HubSections() {
  const haptics = useHaptics();
  const { sections, showBridge, showBurraco, showScopa, showBriscola, showGinRummy, showScala40, showSettemezzo, showTressette, showPilotta, showMpirimpa, showSolitario } = useSezioniDelGioco();
  const dispatch = useAppDispatch();
  const storedSection = useAppSelector((s) => s.core.hubSection) as SectionKey | null;
  // Derive rather than store-and-correct: the section row is game-conditional, so a
  // stored key can point at a section that no longer exists (CR → Chess). Deriving
  // also removes the old setState-in-effect and its one-frame flash of the wrong panel.
  const active: SectionKey =
    (storedSection && sections.some((s) => s.key === storedSection)
      ? storedSection
      : sections[0]?.key) ?? 'rankings';

  const selectSection = (key: SectionKey) => {
    if (key === active) return;
    haptics.selection();
    dispatch(setHubSection(key));
  };

  /**
   * Swipe navigates the button GRID, following its layout:
   *  - horizontal → the next/previous button in READING ORDER across the whole
   *    grid (left→right advances one button; past the last button of a row you
   *    land on the first of the next, and past the very last you wrap to the
   *    very first);
   *  - vertical → the same column one row up/down, wrapping round the rows.
   * Directions are literal: a left→right swipe advances one button, an up→down
   * swipe drops a row.
   *
   * The full 2D gesture lives on the button row (nothing scrolls there to
   * fight); the panel takes only the HORIZONTAL half, so a vertical swipe still
   * scrolls the panel's own content. A pointer-position pair is all we need —
   * the delta between touchdown and lift.
   */
  const COLS = 4;
  const { containerRef: gridRef, rect: gridRect, ready: gridReady } = useSlidingIndicator(active);
  // L'elemento che scorre davvero: il contenitore esterno per le sezioni
  // semplici, quello interno per quelle con `ownScroll`. Registrato al primo
  // scroll, così il tasto "torna in cima" agisce su quello giusto.
  const scrollerRef = useRef<HTMLElement | null>(null);
  const touchRef = useRef<{ x: number; y: number } | null>(null);
  // Set when a swipe was handled on the buttons, so the click the browser
  // synthesises on the button under the finger is swallowed, not re-selected.
  const swipeConsumedRef = useRef(false);
  const SWIPE_MIN_PX = 48;

  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    swipeConsumedRef.current = false;
    touchRef.current = t ? { x: t.clientX, y: t.clientY } : null;
  };

  const gestureDelta = (e: TouchEvent<HTMLDivElement>) => {
    const start = touchRef.current;
    touchRef.current = null;
    const t = e.changedTouches[0];
    if (!start || !t) return null;
    return { dx: t.clientX - start.x, dy: t.clientY - start.y };
  };

  const moveByGesture = (dx: number, dy: number) => {
    const i = sections.findIndex((s) => s.key === active);
    if (i === -1) return;
    let target: SectionDef | undefined;
    if (Math.abs(dx) >= Math.abs(dy)) {
      // Horizontal: linear through the whole grid in reading order, wrap end↔start.
      const n = sections.length;
      target = sections[dx > 0 ? (i + 1) % n : (i - 1 + n) % n];
    } else {
      // Vertical: same column, one row up/down, wrapping the rows.
      const rows = chunk(sections, COLS);
      const row = Math.floor(i / COLS);
      const col = i % COLS;
      const numRows = rows.length;
      const nextRow = dy > 0 ? (row + 1) % numRows : (row - 1 + numRows) % numRows;
      target = rows[nextRow][Math.min(col, rows[nextRow].length - 1)];
    }
    if (target) selectSection(target.key);
  };

  // Buttons: the whole grid (both axes).
  const onButtonsTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    const d = gestureDelta(e);
    if (!d) return;
    if (Math.max(Math.abs(d.dx), Math.abs(d.dy)) < SWIPE_MIN_PX) return;
    swipeConsumedRef.current = true;
    moveByGesture(d.dx, d.dy);
  };

  // Panel: horizontal only — a vertical swipe stays with the panel's own scroll.
  const onPanelTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    const d = gestureDelta(e);
    if (!d) return;
    if (Math.abs(d.dx) < SWIPE_MIN_PX || Math.abs(d.dx) < Math.abs(d.dy) * 1.5) return;
    moveByGesture(d.dx, 0);
  };

  // La compressione del banner e' guidata da <AppShell />, che ascolta lo
  // scroll di TUTTE le rotte. Qui la leggiamo soltanto: serve a nascondere la
  // griglia e a spostare il tasto della sezione accanto al banner.
  const compact = useAppSelector((s) => s.settings.bannerCompact);

  // Registriamo comunque lo scroller attraversato, per sapere su cosa agire
  // quando si tocca il tasto della sezione per tornare in cima.
  const onPanelScroll = (e: UIEvent<HTMLDivElement>) => {
    scrollerRef.current = e.target as HTMLElement;
  };

  // Cambiando sezione si riparte dall'alto: la chrome deve tornare con noi,
  // altrimenti si apre una schermata nuova gia' compressa senza averla scrollata.
  useEffect(() => {
    // Volutamente incondizionato: portarlo a false quando gia' lo e' non
    // produce un nuovo stato in Redux Toolkit, quindi non innesca render.
    // Leggere `compact` qui lo trascinerebbe nelle dipendenze e ogni
    // compressione da scroll si annullerebbe da sola al render successivo.
    dispatch(setBannerCompact(false));
  }, [active, dispatch]);

  // Da OSPITE le sezioni restano (si vede che cosa c'e'), ma nessun pannello si monta: ognuno
  // legge dati legati a un account (tornei, classifiche, partite, mazzi), e al suo posto c'e'
  // «Accedi». Le letture pubbliche sono nelle News (`lib/ospite.ts`, Q2 del 2026-09-25).
  const ospite = useOspite();
  // La Hub del Bridge non ha la riga delle sezioni (v. `HubBridge`): prima di `current`, che di sezioni ne vuole una. In produzione il confronto vale
  // `'false' === 'true'`, il bundler lo piega e `HubBridge` esce dal bundle.
  if (showBridge) return import.meta.env.VITE_FEATURE_BRIDGE_ENABLED === 'true' ? <HubBridge /> : null;
  // La Hub del Burraco: stesso schema. In produzione il confronto vale `'false' === 'true'`, il bundler lo piega e `HubBurraco` esce dal bundle.
  if (showBurraco) return import.meta.env.VITE_FEATURE_BURRACO_ENABLED === 'true' ? <HubBurraco /> : null;
  // La Hub della scopa: stesso schema. In produzione il confronto vale `'false' === 'true'`, il bundler lo piega e i componenti di `HubScopa` escono dal bundle;
  // restano, inerti, le definizioni delle 6 rotte del BFF (`services/limuloScopa.ts`, effetto di modulo di `injectEndpoints`: v. `env.ts`).
  if (showScopa) return import.meta.env.VITE_FEATURE_SCOPA_ENABLED === 'true' ? <HubScopa /> : null;
  // La Hub della briscola: stesso schema. In produzione il confronto vale `'false' === 'true'`, il bundler lo piega e `HubBriscola` esce dal bundle; il modulo delle rotte del BFF
  // (`services/limuloBriscola.ts`) e' marcato PURE e senza consumatori esce anch'esso.
  if (showBriscola) return import.meta.env.VITE_FEATURE_BRISCOLA_ENABLED === 'true' ? <HubBriscola /> : null;
  // La Hub del gin rummy: stesso schema. In produzione il confronto vale `'false' === 'true'`, il bundler lo piega e `HubGinRummy` esce dal bundle.
  if (showGinRummy) return import.meta.env.VITE_FEATURE_GINRUMMY_ENABLED === 'true' ? <HubGinRummy /> : null;
  // La Hub della scala 40: stesso schema. In produzione il confronto vale `'false' === 'true'`, il bundler lo piega e `HubScala40` esce dal bundle.
  if (showScala40) return import.meta.env.VITE_FEATURE_SCALA40_ENABLED === 'true' ? <HubScala40 /> : null;
  // La Hub del sette e mezzo: stesso schema. In produzione il confronto vale `'false' === 'true'`, il bundler lo piega e `HubSettemezzo` esce dal bundle.
  if (showSettemezzo) return import.meta.env.VITE_FEATURE_SETTEMEZZO_ENABLED === 'true' ? <HubSettemezzo /> : null;
  // La Hub del tressette: stesso schema. In produzione il confronto vale `'false' === 'true'`, il bundler lo piega e `HubTressette` esce dal bundle.
  if (showTressette) return import.meta.env.VITE_FEATURE_TRESSETTE_ENABLED === 'true' ? <HubTressette /> : null;
  // La Hub della pilotta: stesso schema. In produzione il confronto vale `'false' === 'true'`, il bundler lo piega e `HubPilotta` esce dal bundle.
  if (showPilotta) return import.meta.env.VITE_FEATURE_PILOTTA_ENABLED === 'true' ? <HubPilotta /> : null;
  // La tessera del Biriba: stesso schema. In produzione il confronto vale `'false' === 'true'`, il bundler lo piega e `HubMpirimpa` esce dal bundle.
  if (showMpirimpa) return import.meta.env.VITE_FEATURE_MPIRIMPA_ENABLED === 'true' ? <HubMpirimpa /> : null;
  // La Hub del solitario (l'hub «Solitari»): stesso schema. In produzione il confronto vale `'false' === 'true'`, il bundler lo piega e `HubSolitari` esce dal bundle.
  if (showSolitario) return import.meta.env.VITE_FEATURE_SOLITARIO_ENABLED === 'true' ? <HubSolitari /> : null;
  const current = sections.find((s) => s.key === active) ?? sections[0];
  if (!current) return null;
  const Panel = current.Panel;
  const CurrentIcon = current.Icon;

  return (
    // Il gap si annulla da compressi: la griglia collassata ha altezza zero, ma
    // il `gap` attorno a lei continua a occupare spazio — 12px sopra e 12px
    // sotto per un elemento che non c'e' piu'. Era la meta' del vuoto fra la
    // chrome e l'intestazione della sezione.
    <div className={cn('flex min-h-0 flex-1 flex-col', compact ? 'gap-0' : 'gap-3')}>
      {/* Da compressi, il tasto della sezione attiva si trasferisce nella testata:
          sparita la griglia, resta l'unica cosa che dice dove sei.
          Tocco = torna in cima, che e' anche il gesto per rivedere la griglia.
          SOLO L'ICONA, 44×44 (la zona tattile minima): sta AL POSTO DEL NOME, dentro la
          striscia della testata, accanto alle monete (L-2705 (b), Luca 02/10 16:31:19Z;
          v. `AppBanner`). Con l'etichetta non ci starebbe. L'etichetta resta il nome
          accessibile: e' la stessa `current.label`, nessun testo nuovo. */}
      <BannerSideSlot>
        <button
          type="button"
          aria-label={current.label}
          onClick={() => {
            haptics.selection();
            scrollerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand text-brand-fg shadow-lg shadow-brand/20"
        >
          <CurrentIcon aria-hidden className="h-5 w-5 shrink-0" strokeWidth={2} />
        </button>
      </BannerSideSlot>

      {/* Section buttons as explicit rows of four, never a scroller (a button
          you can't see doesn't exist). The row that holds the active section
          stays full (icon + label); every OTHER row collapses to label-only —
          reclaiming height in the fixed-viewport Hub. One row (a non-CR game)
          always holds the active section, so it never collapses. */}
      {/* Le due righe vivono DENTRO un unico pannello, non come otto pastiglie
          sciolte sullo sfondo. Il pannello dice "questa e' la navigazione", e
          solo la voce attiva si stacca come pastiglia piena: cosi' l'occhio
          trova subito dove si trova invece di leggere otto cornici uguali. */}
      {/* Scorrendo la griglia si ritrae; tornando in cima ricompare.
          `max-height` invece di `display:none` perche' e' animabile: la griglia
          scivola via invece di sparire di scatto, e il contenuto sotto sale
          seguendola. `pointer-events-none` da chiusa evita che un tocco finisca
          su un bottone alto zero ma ancora presente. */}
      <div
        ref={gridRef}
        role="tablist"
        data-tour="hub-sections"
        aria-hidden={compact}
        className={cn(
          'relative',
          // Vetro: fondo semitrasparente + sfocatura, cosi' la texture a
          // scaglie traspare invece di essere coperta. Il velo e' `bg-bg/55`
          // (teal) e NON `bg-black/25`: il nero traslucido sopra il verde acqua
          // lo desatura e produce il grigio-verde su cui la stroke leggeva
          // grigia. Un velo dello stesso colore scurisce senza spegnere la
          // tinta, quindi si ottiene il vetro senza riportare il grigio.
          'card-edge flex shrink-0 flex-col gap-1 overflow-hidden rounded-2xl bg-bg/55 backdrop-blur-md',
          'transition-all duration-300 ease-out',
          compact ? 'pointer-events-none max-h-0 p-0 opacity-0' : 'max-h-48 p-1.5 opacity-100',
        )}
        onTouchStart={onTouchStart}
        onTouchEnd={onButtonsTouchEnd}
      >
        {/* La pastiglia attiva è UN elemento che si sposta, su due assi: da
            «Ranked» a «Eventi» scorre di lato, da «Missioni» a «Classifica»
            scende di riga. Un fondo che si accendesse sul bottone nuovo direbbe
            solo dove sei ARRIVATO; questo dice anche da dove vieni, che su una
            griglia di otto voci è metà dell'informazione. */}
        {gridRect && !compact && (
          <span
            aria-hidden
            className="absolute rounded-xl bg-brand shadow-lg shadow-brand/20"
            style={{
              left: gridRect.left,
              top: gridRect.top,
              width: gridRect.width,
              height: gridRect.height,
              transition: gridReady ? `all 340ms ${BOUNCE}` : undefined,
            }}
          />
        )}

        {chunk(sections, COLS).map((row, rowIndex) => {
          return (
            <div key={rowIndex} className={cn(RIGA_SCHEDE, 'grid-cols-4')}>
              {row.map((s) => {
                const on = s.key === active;
                return (
                  <button
                    key={s.key}
                    data-tour={`hub-section-${s.key}`}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    data-active={on}
                    onClick={() => {
                      // Swallow the click a swipe-across-buttons synthesises.
                      if (swipeConsumedRef.current) {
                        swipeConsumedRef.current = false;
                        return;
                      }
                      selectSection(s.key);
                    }}
                    className={cn(
                      // `relative` porta icona ed etichetta SOPRA la pastiglia
                      // che scorre; il fondo non sta piu' sul bottone.
                      'relative flex min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1 py-2',
                      // 🔴 Finestre BASSE (<= 640 px: 320 e 360 dentro Telegram): la riga scende da 57 a 44 px, con
                      // l'icona piu' piccola, su TUTTA la Hub e non solo col backgammon (I-334 D, decisione di
                      // layout-tma-orcb 04/10): due righe di schede costano 126 px, e su 504-560 px di finestra sono
                      // quelle che spingevano la «Gioca» del backgammon sotto la piega. Mai sotto 44 (il tocco).
                      'min-h-11 [@media(max-height:640px)]:gap-0.5 [@media(max-height:640px)]:py-1',
                      // Sotto i 360 la scheda prende la larghezza del suo testo (I-362, `schedeSezione.ts`).
                      SCHEDA_STRETTA,
                      'transition-colors active:opacity-80',
                      on ? 'text-brand-fg' : 'text-fg-muted',
                    )}
                  >
                    <s.Icon aria-hidden className="h-5 w-5 shrink-0 [@media(max-height:640px)]:h-4 [@media(max-height:640px)]:w-4" strokeWidth={2} />
                    <span className={ETICHETTA_SCHEDA}>{s.label}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* The only scroller here. overscroll-contain keeps a fling at the end of
          the panel from dragging the Hub around.
          touch-action: pan-y hands vertical scrolling to the browser and leaves
          the horizontal axis to us — without it the swipe fights the scroll and
          both feel broken. */}
      {/* 🔴 Con `ownScroll` il contenitore e' un FLEX colonna, e non per stile: i
          pannelli che portano lo scroller (`QgammonPlayPanel`, `QgammonLeaderboardPanel`, `QcpPlayPanel`,
          i due `*MatchesPanel`, `LeaderboardPanel`) chiedono l'altezza con
          `flex-1 min-h-0`, e su un `div` non-flex quelle classi non fanno
          niente. Il pannello restava alto quanto il contenuto, il suo
          `overflow-y-auto` non aveva un'altezza da cui scorrere, e l'eccedenza
          finiva sotto il taglio di `overflow-hidden` SENZA barra: su un
          telefono basso «Gioca contro bot» del backgammon era irraggiungibile
          (Luca, I-124, 2026-09-23). I pannelli `h-full` (Eventi, Sit&Go,
          Decks, Battaglie) valgono uguale in un flex colonna. */}
      <div
        onScrollCapture={onPanelScroll}
        className={cn(
          'min-h-0 flex-1 [touch-action:pan-y]',
          current.ownScroll ? 'flex flex-col overflow-hidden' : 'overflow-y-auto overscroll-contain',
        )}
        onTouchStart={onTouchStart}
        onTouchEnd={onPanelTouchEnd}
      >
        {ospite ? <RichiedeAccesso forma="sezione" /> : <Panel />}
      </div>
    </div>
  );
}
